/**
 * RolesPermissionsPage - Display roles and their permissions
 * Feature: 001-user-role-management (Phase 5 - T091, T096)
 */
import { useQuery } from '@tanstack/react-query';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Shield, Lock, AlertCircle } from 'lucide-react';
import { roleService } from '../../services/roleService';
import type { RoleWithPermissions } from '../../types/role';

export function RolesPermissionsPage() {
  const { user: clerkUser } = useUser();
  const { getToken } = useAuth();

  // Fetch all roles with permissions
  const {
    data: roles,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['roles'],
    queryFn: () => {
      if (!clerkUser) throw new Error('Not authenticated');
      return roleService.listRoles(() => getToken());
    },
    enabled: !!clerkUser,
  });

  // Helper function to format permission for display
  const formatPermission = (resource: string, action: string, scope: string) => {
    return `${resource}:${action}:${scope}`;
  };

  // Group permissions by resource
  const groupPermissionsByResource = (role: RoleWithPermissions) => {
    const grouped: { [key: string]: typeof role.permissions } = {};

    role.permissions.forEach((perm) => {
      if (!grouped[perm.resource]) {
        grouped[perm.resource] = [];
      }
      grouped[perm.resource].push(perm);
    });

    return grouped;
  };

  return (
    <div className="min-h-screen bg-brand-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-brand-primary" />
            <h1 className="text-3xl font-bold text-brand-text">Roles & Permissions</h1>
          </div>
          <p className="text-sm text-brand-muted">
            View roles and their associated permissions in the system
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 rounded-md bg-brand-error p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-brand-error" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-brand-error">Error loading roles</h3>
                <div className="mt-2 text-sm text-brand-error">
                  {error instanceof Error ? error.message : 'An unexpected error occurred'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
              <p className="mt-4 text-brand-muted">Loading roles...</p>
            </div>
          </div>
        )}

        {/* Roles Grid */}
        {roles && !isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {roles.map((role) => {
              const groupedPermissions = groupPermissionsByResource(role);

              return (
                <div key={role.id} className="bg-brand-bg rounded-lg shadow-md overflow-hidden">
                  {/* Role Header */}
                  <div className="px-6 py-4 bg-brand-primary">
                    <div className="flex items-center gap-2 mb-1">
                      <Lock className="h-5 w-5 text-white" />
                      <h2 className="text-xl font-semibold text-white capitalize">
                        {role.name}
                      </h2>
                      {role.is_system && (
                        <span className="px-2 py-1 text-xs font-medium text-white bg-brand-primary rounded">
                          System
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white/80">
                      {role.description || 'No description'}
                    </p>
                  </div>

                  {/* Permissions List */}
                  <div className="px-6 py-4">
                    <h3 className="text-sm font-medium text-brand-text mb-3">
                      Permissions ({role.permissions.length})
                    </h3>

                    {Object.keys(groupedPermissions).length === 0 ? (
                      <p className="text-sm text-brand-muted italic">No permissions defined</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(groupedPermissions).map(([resource, permissions]) => (
                          <div key={resource} className="border-l-2 border-brand-border pl-3">
                            <h4 className="text-sm font-semibold text-brand-text capitalize mb-1">
                              {resource}
                            </h4>
                            <div className="flex flex-wrap gap-1">
                              {permissions.map((perm) => (
                                <span
                                  key={perm.id}
                                  className="inline-flex items-center px-2 py-1 text-xs font-mono text-brand-text bg-brand-surface rounded"
                                  title={formatPermission(perm.resource, perm.action, perm.scope)}
                                >
                                  {perm.action}
                                  <span className="mx-1 text-brand-muted">:</span>
                                  {perm.scope}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Permissions Summary */}
                  <div className="px-6 py-3 bg-brand-surface border-t border-brand-border">
                    <div className="flex items-center justify-between text-xs text-brand-muted">
                      <span>
                        <strong>{Object.keys(groupedPermissions).length}</strong> resources
                      </span>
                      <span>
                        <strong>{role.permissions.length}</strong> total permissions
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {roles && roles.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <Shield className="h-12 w-12 text-brand-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-brand-text mb-2">No Roles Found</h3>
            <p className="text-brand-muted">
              No roles are currently defined in the system.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
